import { Badge, Banner, Button, Dialog, Field, Input, InputArea, Select, Switch } from '@cloudflare/kumo';
import { FormPage, FormSection } from '../components/ui/FormPage';
import { useCallback, useEffect, useState } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { useAppToast } from '../hooks/useAppToast';
import { DangerConfirmModal } from '../components/DangerConfirmModal';
import { AiTextActions } from '../components/AiAssistant';
import { DangerButton } from '../components/ui/DangerButton';
import { ICONS } from '../components/ui/icons';
import { SkeletonText } from '../components/ui/Skeleton';

interface AutoMessage {
  id: string; name: string; active: boolean; sessionName: string; messages: string[]; hour: number; categoryId: string | null;
  sentThisYear: number; failedThisYear: number;
}
interface Data {
  automations: AutoMessage[]; withBirthday: number;
  sessions: Array<{ name: string; displayName: string | null; status: string; provider: string }>;
  categories: Array<{ id: string; nome: string }>;
  upcoming: Array<{ day: string; contacts: Array<{ id: string; nome: string }> }>;
}

const DEFAULT_TEXT = 'Feliz aniversário, {{primeiro_nome}}! 🎉 Que seu dia seja incrível. Um abraço de toda a nossa equipe!';
const EMPTY = { name: 'Feliz aniversário', sessionName: '', categoryId: '', hour: 9, messages: [DEFAULT_TEXT] };
const HOURS = Array.from({ length: 17 }, (_, i) => i + 6);
/** O `Select` do Cloudscape não aceita valor vazio — usamos uma sentinela. */
const NONE = '__none__';

const dayLabel = (iso: string) => {
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
  if (iso === today) return 'Hoje';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' });
};

/** Contatos › Automações: mensagem de aniversário enviada sozinha todo dia */
export function AutomacoesPage() {
  const { can } = useTenant();
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const [data, setData] = useState<Data | null>(null);
  const [editing, setEditing] = useState<AutoMessage | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState<AutoMessage | null>(null);
  const [testing, setTesting] = useState<AutoMessage | null>(null);
  const [testPhone, setTestPhone] = useState('');
  const canEdit = can('campaigns:write');

  const load = useCallback(async () => {
    const res = await fetchApi('/api/automacoes');
    if (res.ok) setData(await res.json());
  }, [fetchApi]);
  useEffect(() => { load(); }, [load]);

  const startNew = () => {
    setEditing(null);
    setForm({ ...EMPTY, sessionName: data?.sessions.find((s) => s.status === 'WORKING')?.name || '' });
    setOpen(true);
  };
  const startEdit = (a: AutoMessage) => {
    setEditing(a);
    setForm({ name: a.name, sessionName: a.sessionName, categoryId: a.categoryId || '', hour: a.hour, messages: a.messages.length ? a.messages : [DEFAULT_TEXT] });
    setOpen(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = JSON.stringify({ ...form, categoryId: form.categoryId || null, messages: form.messages.filter((m) => m.trim()) });
      const res = editing
        ? await fetchApi(`/api/automacoes/${editing.id}`, { method: 'PATCH', body })
        : await fetchApi('/api/automacoes', { method: 'POST', body });
      const out = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(out.message || 'Não foi possível salvar');
      toast.success(editing ? 'Automação salva' : 'Automação criada: começa a enviar no próximo horário');
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Não foi possível salvar');
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (a: AutoMessage, active: boolean) => {
    const res = await fetchApi(`/api/automacoes/${a.id}`, { method: 'PATCH', body: JSON.stringify({ active }) });
    if (!res.ok) toast.error('Não foi possível salvar'); else load();
  };

  const sendTest = async () => {
    if (!testing) return;
    setBusy(true);
    try {
      const res = await fetchApi(`/api/automacoes/${testing.id}/teste`, { method: 'POST', body: JSON.stringify({ phone: testPhone }) });
      const out = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(out.message || 'Falha no envio');
      toast.success('Mensagem de teste enviada');
      setTesting(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha no envio');
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <div className="py-8"><SkeletonText lines={4} /></div>;
  const sessionLabel = (name: string) => { const s = data.sessions.find((x) => x.name === name); return s?.displayName || name; };
  const catName = (id: string | null) => data.categories.find((c) => c.id === id)?.nome;

  const sessionOptions = data.sessions.map((s) => ({ value: s.name, label: `${s.displayName || s.name}${s.status !== 'WORKING' ? ' (desconectada)' : ''}` }));
  const hourOptions = HOURS.map((h) => ({ value: String(h), label: `${String(h).padStart(2, '0')}:00` }));
  const categoryOptions = [{ value: NONE, label: 'Todos os contatos' }, ...data.categories.map((c) => ({ value: c.id, label: `Categoria ${c.nome}` }))];

  if (open) {
    return (
      <FormPage
        backTo="/automacoes"
        backLabel="Automações"
        onBack={() => setOpen(false)}
        title={editing ? 'Editar automação' : 'Mensagem de aniversário'}
        description="Envia uma mensagem automática no aniversário dos contatos."
        submitLabel={'Salvar'}
        submitting={busy}
        submitDisabled={!form.sessionName || !form.messages.some((m) => m.trim())}
        onSubmit={save}
        onCancel={() => setOpen(false)}
      >
        <FormSection title="Detalhes">


        <div className="flex flex-col gap-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Nome">
              <Input onChange={(e) => setForm({ ...form, name: e.target.value })} value={form.name} />
            </Field>
            <Field label="Conexão que envia">
              <Select value={(sessionOptions.find((o) => o.value === form.sessionName) ?? null)?.value ?? null} items={sessionOptions} onValueChange={(value: string | null) => {
                  const v = value;
                  if (!v) return;
                  setForm({ ...form, sessionName: v });
                }} placeholder="Selecione a conexão" />
            </Field>
            <Field label="Horário">
              <Select value={(hourOptions.find((o) => o.value === String(form.hour)) ?? null)?.value ?? null} items={hourOptions} onValueChange={(value: string | null) => {
                  const v = value;
                  if (!v) return;
                  setForm({ ...form, hour: Number(v) });
                }} />
            </Field>
            <Field label="Para quem">
              <Select value={(categoryOptions.find((o) => o.value === (form.categoryId || NONE)) ?? null)?.value ?? null} items={categoryOptions} onValueChange={(value: string | null) => {
                  const v = value;
                  if (!v) return;
                  setForm({ ...form, categoryId: v === NONE ? '' : v });
                }} />
            </Field>
          </div>
          {form.messages.map((m, i) => (
            <div key={i}>
              <InputArea label={form.messages.length > 1 ? `Mensagem (variação ${i + 1})` : 'Mensagem'} description={i === 0 ? 'Use {{primeiro_nome}} ou {{nome}}. Com mais de uma variação, cada contato recebe uma sorteada.' : undefined} onChange={(e) => setForm({ ...form, messages: form.messages.map((x, j) => (j === i ? e.target.value : x)) })} value={m} rows={3} />
              <div className="flex items-center gap-2" style={{ marginTop: 4 }}>
                <AiTextActions text={m} onText={(t) => setForm({ ...form, messages: form.messages.map((x, j) => (j === i ? t : x)) })}
                  onVariations={i === 0 ? (v) => setForm({ ...form, messages: [m, ...v].filter((x) => x.trim()).slice(0, 5) }) : undefined} />
                {form.messages.length > 1 && <DangerButton variant="dangerOutline" onClick={() => setForm({ ...form, messages: form.messages.filter((_, j) => j !== i) })}>Remover</DangerButton>}
              </div>
            </div>
          ))}
          {form.messages.length < 5 && <div><Button variant="secondary" icon={ICONS.Plus} onClick={() => setForm({ ...form, messages: [...form.messages, ''] })}>Adicionar variação</Button></div>}
        </div>
        
        </FormSection>
      </FormPage>
    );
  }

  return (
    <>
      <div style={{ padding: '28px 40px 0', marginBottom: 24 }}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-xl font-semibold" style={{ marginBottom: 4, fontSize: 24, fontWeight: 600 }}>Automações</h2>
            <div style={{ color: 'var(--text-muted)', maxWidth: 760 }}>
              Mensagens que saem sozinhas. A de aniversário é enviada todo dia, no horário escolhido, para os contatos que fazem aniversário, uma vez por ano. Quem pediu para sair não recebe.
            </div>
          </div>
          {canEdit && <Button variant="primary" icon={ICONS.Plus} onClick={startNew}>Nova automação</Button>}
        </div>
      </div>

      <div className="page-content space-y-6">
        {data.withBirthday === 0 && (
          <Banner variant="alert" description={<>
            Nenhum contato tem data de nascimento. Preencha no cadastro do contato ou importe um CSV com a coluna <code>dataNascimento</code> (DD/MM/AAAA ou só DD/MM).
          </>} />
        )}

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-4">
            {data.automations.length === 0 && <Banner variant="default" description={<>Nenhuma automação ainda. Crie a mensagem de aniversário: leva um minuto.</>} />}
            {data.automations.map((a) => (
              <section key={a.id} className="brand-card" style={{ padding: 20 }}>
                <div className="flex items-start justify-between gap-3">
                  <div style={{ minWidth: 0 }}>
                    <h4 className="text-base font-semibold">🎂 {a.name}</h4>
                    <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                      Todo dia às {String(a.hour).padStart(2, '0')}h · por {sessionLabel(a.sessionName)}{catName(a.categoryId) ? ` · só categoria ${catName(a.categoryId)}` : ' · todos os contatos'}
                    </div>
                  </div>
                  <Badge variant={a.active ? 'green' : 'neutral'}>{a.active ? 'Ativa' : 'Pausada'}</Badge>
                </div>
                <div style={{ marginTop: 10, fontSize: 14, whiteSpace: 'pre-wrap' }}>“{a.messages[0]}”{a.messages.length > 1 ? ` (+${a.messages.length - 1} variação${a.messages.length > 2 ? 'ões' : ''})` : ''}</div>
                <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)' }}>
                  Este ano: {a.sentThisYear} enviada{a.sentThisYear !== 1 ? 's' : ''}{a.failedThisYear ? ` · ${a.failedThisYear} com falha` : ''}
                </div>
                {canEdit && (
                  <div className="flex items-center justify-between flex-wrap gap-2" style={{ marginTop: 12 }}>
                    <label className="flex items-center gap-2"><Switch onCheckedChange={(checked: boolean) => toggle(a, checked)} aria-label="Ativa" checked={a.active} /><div style={{ fontSize: 13 }}>Ativa</div></label>
                    <div className="flex gap-2">
                      <Button variant="secondary" icon={ICONS.Megaphone} onClick={() => { setTesting(a); setTestPhone(''); }}>Testar</Button>
                      <Button variant="secondary" icon={ICONS.Edit} onClick={() => startEdit(a)}>Editar</Button>
                      <DangerButton variant="dangerOutline" onClick={() => setRemoving(a)}>Excluir</DangerButton>
                    </div>
                  </div>
                )}
              </section>
            ))}
          </div>

          <section className="brand-card" style={{ padding: 20, alignSelf: 'start' }}>
            <h4 className="text-base font-semibold" style={{ marginBottom: 4 }}>Próximos aniversariantes</h4>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>{data.withBirthday.toLocaleString('pt-BR')} contato{data.withBirthday !== 1 ? 's' : ''} com data de nascimento</div>
            {data.upcoming.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Ninguém nos próximos 7 dias.</div>}
            <ul className="space-y-3">
              {data.upcoming.map((u) => (
                <li key={u.day}>
                  <div className="font-semibold" style={{ fontSize: 13, textTransform: 'capitalize' }}>{dayLabel(u.day)}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-2)' }}>{u.contacts.map((c) => c.nome).join(', ')}</div>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>



      <Dialog.Root open={!!testing} onOpenChange={(o: boolean) => { if (!o) (() => setTesting(null))(); }}>
<Dialog size="sm" className="p-6">
<Dialog.Title className="mb-4 text-lg font-semibold">{<h3 className="text-lg font-semibold">Enviar teste</h3>}</Dialog.Title>

        <Field label="Número com DDD">
          <Input onChange={(e) => setTestPhone(e.target.value)} placeholder="63 99999-0000" value={testPhone} />
        </Field>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>Vai com o nome “Maria Silva” no lugar das variáveis.</div>
        <div className="flex justify-end gap-2" style={{ marginTop: 20 }}>
          <Button variant="secondary" type="button" onClick={() => setTesting(null)}>Cancelar</Button>
          <Button variant="primary" loading={busy} disabled={testPhone.replace(/\D/g, '').length < 10} onClick={sendTest}>Enviar</Button>
        </div>
      
</Dialog>
</Dialog.Root>

      <DangerConfirmModal open={!!removing} title="Excluir automação" confirmText="Excluir" objectName={removing?.name} onCancel={() => setRemoving(null)}
        onConfirm={async () => { const a = removing; setRemoving(null); if (a) { const r = await fetchApi(`/api/automacoes/${a.id}`, { method: 'DELETE' }); if (r.ok) { toast.success('Automação excluída'); load(); } } }}>
        {removing ? `A mensagem "${removing.name}" deixa de ser enviada.` : ''}
      </DangerConfirmModal>
    </>
  );
}
